import * as React from 'react';

import { Field, FormikActions } from 'formik';
import { InputGroup, InputGroupAddon } from '@lightspeed/flame/InputGroup';
import { Input } from '@lightspeed/flame/Input';
import { Divider } from '@lightspeed/flame/Divider';
import { AnnotatedLayout } from '@lightspeed/flame/Layout';
import { Card, CardSection } from '@lightspeed/flame/Card';
import { Text, TextLink } from '@lightspeed/flame/Text';
import { Flex, flameTheme } from '@lightspeed/flame/Core';
import { translate } from 'pages/utils/translate';

import FormikInput from 'pages/components/Formik/Input';
import { useTipSettings } from './store';
import { TipOptionEnum } from './store/type';

const TipOptionLabel = [
  {
    name: TipOptionEnum.option1,
    label: translate('Option 1'),
  },
  {
    name: TipOptionEnum.option2,
    label: translate('Option 2'),
  },
  {
    name: TipOptionEnum.option3,
    label: translate('Option 3'),
  },
];

export const TipOptions = () => {
  const { tipToggle } = useTipSettings();
  if (!tipToggle) {
    return null;
  }
  return (
    <>
      <Divider my={8} />
      <AnnotatedLayout
        title={translate('Tip options')}
        description={translate(
          'Set custom tip percentages. These will be visible to customers on your Customer Facing Display.'
        )}
        renderExtras={
          <TextLink
            href="https://retail-support.lightspeedhq.com/hc/articles/4402387834395"
            target="_blank"
          >
            {translate(`Learn more`)}
          </TextLink>
        }
      >
        <Card>
          <CardSection>
            <Flex my={2} mx={2} flexDirection="column">
              <Text mb={3} fontWeight="bold" color={flameTheme.colors.black}>
                {translate('Tip options')}
              </Text>
              {TipOptionLabel.map(({ name, label }) => (
                <TipOption name={name} key={name} label={label} />
              ))}
            </Flex>
          </CardSection>
        </Card>
      </AnnotatedLayout>
    </>
  );
};

const TipOption = ({ name, label }: { name: string; label: string }) => {
  return (
    <Flex mb={3} flexDirection="column">
      <Field
        name={name}
        render={(fieldProps: any) => (
          <FormikInput {...fieldProps} label={label}>
            {(inputProps: any) => (
              <InputGroup mt={1} fontWeight="bold">
                <InputGroupAddon>%</InputGroupAddon>
                <CustomTipInput
                  aria-label={`${label} input box`}
                  {...inputProps}
                  color={flameTheme.colors.black}
                />
              </InputGroup>
            )}
          </FormikInput>
        )}
      />
    </Flex>
  );
};

const CustomTipInput = ({
  value: previousValue,
  name,
  setFieldValue,
  setFieldTouched,
  ...props
}: {
  value: number;
  name: string;
  setFieldValue: FormikActions<number>['setFieldValue'];
  setFieldTouched: FormikActions<number>['setFieldTouched'];
}) => {
  const [internalValue, setInternalValue] = React.useState(previousValue);

  React.useEffect(() => {
    setInternalValue(previousValue);
  }, [previousValue]);

  const handleOnChange = (event: React.ChangeEvent<any>) => {
    const sanitizedInputValue = event.target.value.replace(/[^\d.]/, '');
    const allowInputPattern = /^[0-9\b]+$/;

    if (sanitizedInputValue.match(allowInputPattern) || sanitizedInputValue === '') {
      setInternalValue(sanitizedInputValue);
    }
  };

  const handleOnBlur = () => {
    if (Number(previousValue) !== Number(internalValue)) {
      setFieldValue(name, internalValue);
      setFieldTouched(name, true);
    }
  };

  const handleOnFocusAmount = (event: React.ChangeEvent<any>) => {
    event.target.select();
  };

  const handleOnKeyPressAmount = (event: React.KeyboardEvent<any>) => {
    // prevent the global keypress handler from firing
    event.stopPropagation();
    if (event.key === 'Enter') {
      // blurring the input will trigger the handleAmountBlur method
      event.currentTarget.blur();
    }
  };

  return (
    <Input
      {...props}
      autoComplete="off"
      value={internalValue}
      onFocus={handleOnFocusAmount}
      onKeyPress={handleOnKeyPressAmount}
      onBlur={handleOnBlur}
      onChange={handleOnChange}
    />
  );
};
